/**
 * The requirement rows: a table on desktop, cards below 768px.
 *
 * ONE COPY IN THE DOM, NOT TWO. Which layout renders is decided in JavaScript by
 * `useIsMobile` — the repo's locked 768px hook — rather than by a
 * `hidden md:block` / `md:hidden` pair. Both look right in a browser, but the
 * CSS version leaves every row in the document twice, and anything walking the
 * DOM without a stylesheet (a test, a crawler, a screen reader in browse mode)
 * then sees each requirement doubled with no way to tell which copy is real.
 * `TenantDepositsTable` records the same decision and the same reason.
 *
 * NOTHING HERE DECIDES ANYTHING. Every value on screen is a field of the S4
 * response: the badge is `row.status`, the deadline is `row.effective_due_date`,
 * the evidence subject is `row.evidence_tenant_name`. There is no comparison
 * against today, no reading of `matching_document_count` to conclude whether a
 * requirement is met, and no second opinion about a deposit. A row that says
 * "Erfüllt" says it because the backend does.
 *
 * TWO CONTEXTS, ONE TABLE. `contextMode` says what the surrounding page has
 * ALREADY told the reader, and nothing else:
 *
 *   "global"       (default) — the workspace list. Every row has to identify
 *                  itself, so tenant, tenancy and object are columns.
 *   "tenancyGroup" — the tenant detail tab, where the rows are already under a
 *                  heading naming that one tenancy. Repeating the same three
 *                  values on every row would print the heading three times.
 *
 * It is a PRESENTATION switch. It changes no status, no deadline, no count and
 * no action — swapping it must never change what a row MEANS, only how much of
 * its context the row has to restate. The default is the S6 behaviour verbatim,
 * so a caller that passes nothing is unaffected.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";

import { useIsMobile } from "../../../hooks/useIsMobile";
import { formatDateOnly } from "../../../utils/datetime";
import RequirementDetailTrigger from "./RequirementDetailTrigger";
import {
  requirementStatusLabel,
  requirementStatusTone,
  requirementTypeLabel,
} from "./requirementLabels";

const HEAD =
  "px-2.5 py-2.5 text-left text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted-foreground";
const CELL = "px-2.5 py-2.5 align-top text-[12.5px] text-foreground";

/**
 * A status pill. Text first, colour second — the label is always spelled out, so
 * a reader who cannot tell amber from rose loses no information.
 *
 * The tone vocabulary is `DepositBadge`'s, deliberately, because it is the
 * repo's current badge convention. It is restated here rather than imported: a
 * component whose name says "Deposit" appearing on every requirement row would
 * be the wrong word in the source for the sake of eight class strings.
 */
const TONES = {
  positive:
    "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300",
  warn: "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300",
  bad: "border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300",
  neutral: "border-border bg-muted text-foreground",
  muted: "border-border bg-transparent text-muted-foreground",
};

function StatusBadge({ status }) {
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
        TONES[requirementStatusTone(status)] || TONES.neutral
      }`}
    >
      {requirementStatusLabel(status)}
    </span>
  );
}

/**
 * The row's ⋮ menu.
 *
 * The same accessibility structure as `TenancyRowActions` — trigger with
 * `aria-haspopup="menu"` / `aria-expanded` / `aria-label`, a click-away layer,
 * `role="menu"` over `role="menuitem"` buttons — written here rather than
 * imported because this surface needs one thing that component does not offer:
 * a trigger that is genuinely `disabled` while a write is in flight. Adding the
 * prop over there would change a component two other pages already render for
 * the sake of a page it knows nothing about.
 */
function RowActions({ items, label = "Aktionen", disabled = false }) {
  const [open, setOpen] = useState(false);
  const visible = (items || []).filter(Boolean);
  if (visible.length === 0) return null;

  return (
    <div className="relative inline-block text-left">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="flex h-[26px] w-[26px] items-center justify-center rounded-[6px] text-[15px] leading-none text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-primary/40 disabled:cursor-not-allowed disabled:opacity-40"
      >
        ⋮
      </button>

      {open ? (
        <>
          <button
            type="button"
            tabIndex={-1}
            aria-hidden="true"
            className="fixed inset-0 z-40 cursor-default"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
            }}
          />
          <div
            role="menu"
            className="absolute right-0 z-50 mt-1 min-w-[196px] overflow-hidden rounded-[8px] border border-border bg-card py-1 shadow-lg"
          >
            {visible.map((item) => (
              <button
                key={item.key}
                type="button"
                role="menuitem"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  item.onSelect();
                }}
                className={`block w-full px-3 py-[6px] text-left text-[12.5px] hover:bg-muted ${
                  item.destructive
                    ? "text-rose-700 dark:text-rose-300"
                    : "text-foreground"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}

/** `tenancy × requirement_type` is the identity — there is no row id. */
export function rowKey(row) {
  return `${row.tenancy_id}:${row.requirement_type}`;
}

function objectPrimary(row) {
  return String(row.unit_short_id || row.unit_title || "").trim() || "—";
}

/** The second line under the object: the title when the short id took the first
 *  line, and the room. Both are S4 fields; neither is derived. */
function objectSecondary(row) {
  const parts = [];
  if (row.unit_title && row.unit_title !== objectPrimary(row)) {
    parts.push(row.unit_title);
  }
  if (row.room_name) parts.push(row.room_name);
  return parts.join(" · ");
}

/**
 * Whose policy the liability row is about (S4 `evidence_tenant_name`).
 *
 * Backend-resolved and shown verbatim. Working it out here from the current
 * tenant or the participants would be a second answer to a question the read
 * model already answered, free to disagree with it on exactly the shared
 * tenancies where it matters.
 */
function EvidenceSubject({ row }) {
  if (row.requirement_type !== "liability_insurance") return null;
  if (!row.evidence_tenant_name) return null;
  return (
    <span className="mt-0.5 block text-[11px] text-muted-foreground">
      {`Haftpflichtnachweis von: ${row.evidence_tenant_name}`}
    </span>
  );
}

/**
 * The deadline cell: the effective date, and what made it that.
 *
 * `days_until_due` is deliberately not rendered. Wording it ("noch 3 Tage",
 * "3 Tage überfällig") means reading the sign of a number to say whether
 * something is late — which is what `status` already says, from the engine that
 * owns the question.
 */
function DueDate({ row }) {
  return (
    <>
      <span className="block">
        {row.effective_due_date ? formatDateOnly(row.effective_due_date) : "Keine Frist"}
      </span>
      {row.due_date_override ? (
        <span className="mt-0.5 block text-[11px] text-muted-foreground">
          Individuelle Frist
        </span>
      ) : null}
    </>
  );
}

/**
 * The evidence cell.
 *
 * A DEPOSIT IS NOT A DOCUMENT. Its truth is the tenancy's own deposit fields,
 * which the backend classified into `status`; a deposit can be fulfilled with no
 * document filed anywhere. Printing "kein passender Nachweis" against a row the
 * badge calls "Erfüllt" would put two contradictory statements side by side and
 * make the correct one look like the mistake. So the deposit row names where its
 * truth lives instead, and the badge stays the answer.
 *
 * `reason_code` is never printed. It is an engine token, not a sentence.
 */
function EvidenceCell({ row }) {
  if (row.requirement_type === "deposit") {
    return (
      <>
        <span className="block">Kautionsstatus</span>
        <span className="mt-0.5 block text-[11px] text-muted-foreground">
          Systemgeführt
        </span>
      </>
    );
  }
  const count = Number(row.matching_document_count) || 0;
  if (count === 0) {
    return <span className="block text-muted-foreground">Kein passender Nachweis</span>;
  }
  return <span className="block">{count === 1 ? "1 Dokument" : `${count} Dokumente`}</span>;
}

/**
 * The tenant's name, as a real link to their file.
 *
 * A LINK, NOT A CLICK HANDLER. `/admin/tenants/:id` exists, so this is an
 * anchor: middle-click opens a tab, the status bar shows where it goes, and the
 * keyboard reaches it without the row needing a role it should not have. The
 * encoding follows `AdminTenanciesPage`, which encodes the same segment.
 *
 * `stopPropagation` because the row underneath opens the drawer, and going to
 * the person is a different intention from inspecting the requirement. Without
 * it a click would navigate away AND leave a drawer open behind the transition.
 *
 * No chevron: the row already carries one on its Details trigger, and two
 * chevrons in one row is two invitations with nothing to distinguish them.
 */
function TenantCell({ row }) {
  const name = row.tenant_name || "—";
  if (!row.tenant_id) return <span>{name}</span>;
  return (
    <Link
      to={`/admin/tenants/${encodeURIComponent(row.tenant_id)}`}
      onClick={(e) => e.stopPropagation()}
      data-testid={`requirement-tenant-link-${rowKey(row)}`}
      className="rounded-[4px] text-foreground underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
    >
      {name}
    </Link>
  );
}

/**
 * When somebody last asked for this proof — `row.last_requested_at`, verbatim.
 *
 * NOT DERIVED FROM ANY TIMELINE. The backend aggregates requests and reminders
 * only, so a note never moves this value; recomputing it here from the contact
 * history would produce a second answer that disagrees precisely when somebody
 * wrote a note after the last reminder.
 *
 * "Noch nicht angefragt" is shown only where not having asked is a TASK. On a
 * fulfilled, excused or inapplicable requirement nobody needs to ask, and
 * printing it there would turn a finished row into an accusation. A historical
 * date, once there is one, stays visible in every status — it happened.
 */
function FollowUpNote({ row }) {
  if (row.last_requested_at) {
    return (
      <span
        className="mt-0.5 block text-[11px] text-muted-foreground"
        data-testid={`requirement-last-requested-${rowKey(row)}`}
      >
        {`Zuletzt angefragt: ${formatDateOnly(row.last_requested_at)}`}
      </span>
    );
  }
  if (row.status !== "open" && row.status !== "overdue") return null;
  return (
    <span
      className="mt-0.5 block text-[11px] text-muted-foreground"
      data-testid={`requirement-never-requested-${rowKey(row)}`}
    >
      Noch nicht angefragt
    </span>
  );
}

/** The waiver note under the badge. `waive_reason` is the admin's own sentence,
 *  not an engine code. */
function WaiveNote({ row }) {
  if (!row.waived_at) return null;
  return (
    <span className="mt-0.5 block text-[11px] text-muted-foreground">
      {row.waive_reason ? `Erlassen: ${row.waive_reason}` : "Erlassen"}
    </span>
  );
}

function buildItems(row, actions) {
  if (row.waived_at) {
    return [
      { key: "due", label: "Frist ändern", onSelect: () => actions.onEditDueDate(row) },
      {
        key: "reason",
        label: "Begründung bearbeiten",
        onSelect: () => actions.onEditReason(row),
      },
      {
        key: "unwaive",
        label: "Erlass aufheben",
        onSelect: () => actions.onUnwaive(row),
      },
    ];
  }
  return [
    { key: "due", label: "Frist ändern", onSelect: () => actions.onEditDueDate(row) },
    {
      key: "waive",
      label: "Pflicht erlassen",
      destructive: true,
      onSelect: () => actions.onWaive(row),
    },
  ];
}

function DesktopTable({ rows, actions, busyKey, showContext, onOpenDetails }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <caption className="sr-only">Nachweise und Pflichten</caption>
        <thead>
          <tr className="border-b border-border">
            {showContext ? (
              <>
                <th scope="col" className={HEAD}>Mieter</th>
                <th scope="col" className={HEAD}>Mietverhältnis</th>
                <th scope="col" className={HEAD}>Objekt / Zimmer</th>
              </>
            ) : null}
            <th scope="col" className={HEAD}>Nachweistyp</th>
            <th scope="col" className={HEAD}>Status</th>
            <th scope="col" className={HEAD}>Frist</th>
            <th scope="col" className={HEAD}>Nachweis</th>
            <th scope="col" className={`${HEAD} text-right`}>
              <span className="sr-only">Aktionen</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={rowKey(row)}
              data-testid={`requirement-row-${rowKey(row)}`}
              // MOUSE COMFORT ONLY. Deliberately no role="button" and no
              // tabIndex: both would lie about what a <tr> is — the first
              // destroys the row/column semantics a screen reader announces,
              // the second adds a tab stop with no announced role. The
              // keyboard path is the real <button> in the last cell.
              onClick={onOpenDetails ? () => onOpenDetails(row) : undefined}
              className={`border-b border-border/60 last:border-0 hover:bg-muted/40 ${
                onOpenDetails ? "cursor-pointer" : ""
              }`}
            >
              {showContext ? (
                <>
                  <td className={`${CELL} font-medium`}>
                    <TenantCell row={row} />
                  </td>
                  <td className={`${CELL} text-muted-foreground`}>
                    {row.tenancy_reference_number || "—"}
                  </td>
                  <td className={CELL}>
                    {objectPrimary(row)}
                    {objectSecondary(row) ? (
                      <span className="mt-0.5 block text-[11px] text-muted-foreground">
                        {objectSecondary(row)}
                      </span>
                    ) : null}
                  </td>
                </>
              ) : null}
              <td className={CELL}>
                {requirementTypeLabel(row.requirement_type)}
                <EvidenceSubject row={row} />
              </td>
              <td className={CELL}>
                <StatusBadge status={row.status} />
                <WaiveNote row={row} />
                <FollowUpNote row={row} />
              </td>
              <td className={CELL}>
                <DueDate row={row} />
              </td>
              <td className={CELL}>
                <EvidenceCell row={row} />
              </td>
              <td className={`${CELL} text-right`}>
                <div className="flex items-center justify-end gap-1">
                  <RequirementDetailTrigger row={row} onOpen={onOpenDetails} />
                  <RowActions
                    items={buildItems(row, actions)}
                    label={`Aktionen für ${requirementTypeLabel(row.requirement_type)} von ${
                      row.tenant_name || "Mieter"
                    }`}
                    disabled={busyKey === rowKey(row)}
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MobileCards({ rows, actions, busyKey, showContext, onOpenDetails }) {
  return (
    <ul className="m-0 list-none space-y-2 p-0">
      {rows.map((row) => (
        <li
          key={rowKey(row)}
          data-testid={`requirement-row-${rowKey(row)}`}
          // Same contract as the desktop row: a tap outside the card's own
          // controls opens the drawer, and the card stays a plain <li> — no
          // role="button", because the real button sits in the footer and the
          // controls inside stop the tap from reaching here.
          onClick={onOpenDetails ? () => onOpenDetails(row) : undefined}
          className="rounded-lg border border-border p-3"
        >
          {/* The card's first line is whatever identifies the row in THIS
              context: the person on the workspace list, the requirement itself
              once a tenancy heading is already carrying the rest. The badge
              keeps its place on the right either way. */}
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              {showContext ? (
                <>
                  <p className="m-0 text-[13px] font-medium text-foreground">
                    <TenantCell row={row} />
                  </p>
                  <p className="m-0 text-[11px] text-muted-foreground">
                    {row.tenancy_reference_number || "—"}
                  </p>
                </>
              ) : (
                <p className="m-0 text-[13px] font-medium text-foreground">
                  {requirementTypeLabel(row.requirement_type)}
                </p>
              )}
            </div>
            <div className="shrink-0 text-right">
              <StatusBadge status={row.status} />
            </div>
          </div>

          {showContext ? (
            <p className="mt-2 text-[12px] text-foreground">
              {objectPrimary(row)}
              {objectSecondary(row) ? (
                <span className="text-muted-foreground">{` · ${objectSecondary(row)}`}</span>
              ) : null}
            </p>
          ) : null}

          <p className="mt-2 m-0 text-[12px] text-foreground">
            {showContext ? requirementTypeLabel(row.requirement_type) : null}
            <EvidenceSubject row={row} />
          </p>

          <div className="mt-2 grid grid-cols-2 gap-2 text-[12px] text-foreground">
            <div>
              <span className="block text-[10.5px] uppercase tracking-[0.06em] text-muted-foreground">
                Frist
              </span>
              <DueDate row={row} />
            </div>
            <div>
              <span className="block text-[10.5px] uppercase tracking-[0.06em] text-muted-foreground">
                Nachweis
              </span>
              <EvidenceCell row={row} />
            </div>
          </div>

          <WaiveNote row={row} />
          <FollowUpNote row={row} />

          <div className="mt-2 flex items-center justify-end gap-1">
            <RequirementDetailTrigger row={row} onOpen={onOpenDetails} />
            <RowActions
              items={buildItems(row, actions)}
              label={`Aktionen für ${requirementTypeLabel(row.requirement_type)} von ${
                row.tenant_name || "Mieter"
              }`}
              disabled={busyKey === rowKey(row)}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

export default function RequirementsTable({
  rows,
  actions,
  busyKey,
  contextMode = "global",
  onOpenDetails,
}) {
  const isMobile = useIsMobile();
  // Anything other than the one narrowed mode shows the full context: an
  // unknown value must not silently hide columns.
  const showContext = contextMode !== "tenancyGroup";
  if (!rows || rows.length === 0) return null;
  return isMobile ? (
    <MobileCards
      rows={rows}
      actions={actions}
      busyKey={busyKey}
      showContext={showContext}
      onOpenDetails={onOpenDetails}
    />
  ) : (
    <DesktopTable
      rows={rows}
      actions={actions}
      busyKey={busyKey}
      showContext={showContext}
      onOpenDetails={onOpenDetails}
    />
  );
}
